import {
    createListenerMiddleware as createListenerMiddlewareCore,
    addListener,
    AnyAction,
} from '@reduxjs/toolkit';
import type { TypedStartListening, TypedAddListener } from '@reduxjs/toolkit';
import { MatchFunction } from '@reduxjs/toolkit/dist/listenerMiddleware/types';
import {
    AnyListenerPredicate,
    ListenerEffect,
} from '@reduxjs/toolkit/src/listenerMiddleware/types';
import State from './State';
import { IndigoDispatch } from 'store/store';
import Services from 'store/Services';

export type AppStartListening = TypedStartListening<State, IndigoDispatch>;

export const createListenerMiddleware = (services: Services) =>
    createListenerMiddlewareCore({ extra: services });

export type ListenerMiddleware = ReturnType<typeof createListenerMiddleware>;

export const startAppListening = (middleware: ListenerMiddleware) =>
    middleware.startListening as AppStartListening;

export const addAppListener = addListener as TypedAddListener<
    State,
    IndigoDispatch
>;

export type ActionEffectOption<TActions extends AnyAction> = {
    startListening: AppStartListening;
    matcher: MatchFunction<TActions>;
    effect: (action: TActions, dispatch: IndigoDispatch, state: State) => void;
};

export type ActionEffectConfig<TActions extends AnyAction> = {
    matcher: MatchFunction<TActions>;
    effect: (
        action: TActions,
        dispatch: IndigoDispatch,
        state: State,
        services: Services
    ) => void;
};

/*
 * Configure an action effect, that runs when an action matches the supplied "matcher"
 */
export function actionEffect<TActions extends AnyAction>(
    matcher: MatchFunction<TActions>,
    effect: (
        action: TActions,
        dispatch: IndigoDispatch,
        state: State,
        services: Services
    ) => void
): ActionEffectConfig<TActions> {
    return { matcher, effect };
}

/*
 * Register an ActionEffectConfig with the middleware
 */
export function registerActionEffect<T extends AnyAction>(
    middleware: ListenerMiddleware,
    effectConfig: ActionEffectConfig<T>
) {
    const { matcher, effect } = effectConfig;
    startAppListening(middleware)({
        matcher,
        effect: async (action, listener) => {
            if (matcher(action)) {
                // Work around redux toolkit typing shortcoming i.e. we know what this action is!
                effect(
                    action,
                    listener.dispatch,
                    listener.getState(),
                    listener.extra as Services
                );
            }
        },
    });
}

export type PredicateEffectConfig = {
    predicate: AnyListenerPredicate<State>;
    effect: ListenerEffect<AnyAction, State, IndigoDispatch, Services>;
};

/*
 * Configure a predicate effect, allowing triggering logic when some state value has changed
 */
export function predicateEffect(
    predicate: AnyListenerPredicate<State>,
    effect: ListenerEffect<AnyAction, State, IndigoDispatch, Services>
): PredicateEffectConfig {
    return { predicate, effect };
}

export function registerPredicateEffect(
    middleware: ListenerMiddleware,
    effectConfig: PredicateEffectConfig
) {
    const { predicate, effect } = effectConfig;
    startAppListening(middleware)({
        predicate,
        effect,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
}
